import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { AudienceFilterApp } from 'app/audience/components/audience.model';
import { Automation, AutomationEditorConfig } from 'app/automations/automations-models';
import { IntegrationType } from 'app/automations/integrations-models';
import { Account } from 'app/back-office/account.model';
import {
  AlternativeStepIntegration,
  ButtonActionType,
  ButtonComponent,
  FormSettingsHandlerTypes,
  StatesInUse,
  Widget,
  WidgetComponent,
  WidgetState,
} from 'app/campaign/widgets.model';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { WidgetAutomationsService } from 'app/campaign/widget-automations.service';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { OutcomeLogicComponent } from 'app/campaign/widget-editor/outcome-logic/outcome-logic.component';
import { AccountLanguageService } from 'app/core/account-language.service';
import { LanguageService } from 'app/core/language.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { FlyoutService } from '../../flyout.service';
import { AutomationSerializer } from '../services/automation-serializer';
import { HandlerRealTimeProp } from './model/handler-real-time-prop';
import { RealTimeHandlerEntry } from './model/real-time-handler-entry';
import { SubmitButtonLocation } from './model/submit-button-location';
import {
  HANDLER_ORDER,
  applyHandlerActionChange,
  buildHandlerView,
  isHandlerSaveDisabled,
  markInvalidSteps,
  toFormSettingsPayload,
} from './model/real-time-handler';
import {
  buildChoosableStates,
  deriveNewSubscriberState,
  findSubmitButtonLocation,
  integrationHasLists,
} from './model/show-message-transforms';

const ESP_ALTERNATIVE_APPS: ReadonlyArray<string> = [
  AlternativeStepIntegration.MailChimp,
  AlternativeStepIntegration.Rule,
  AlternativeStepIntegration.Voyado,
  AlternativeStepIntegration.Emarsys,
  AlternativeStepIntegration.Custobar,
  AlternativeStepIntegration.MailerLite,
  AlternativeStepIntegration.Klaviyo,
];

/** Only offered when their registration is the active `formRegistrationType`: you can only alternate to an eCommerce step that's set up. */
const ECOMMERCE_ALTERNATIVE_APPS: ReadonlyArray<string> = [
  AlternativeStepIntegration.Centra,
  AlternativeStepIntegration.Shopify,
  AlternativeStepIntegration.Shopware,
];

@Component({
  selector: 'app-flyout-form-settings-show-message',
  templateUrl: './flyout-form-settings-show-message.component.html',
  styleUrl: './flyout-form-settings-show-message.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('expandReveal', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class FlyoutFormSettingsShowMessageComponent implements OnChanges {
  @Input() public widget: Widget | null = null;
  @Input() public widgetState!: WidgetState;
  @Input() public account: Account | null = null;
  @Input() public existingAutomation: Automation | null = null;
  @Input() public configuredApps: AutomationEditorConfig[] = [];
  /** Apps the account has an integration connected for, configured on this widget or not. */
  @Input() public availableApps: AutomationEditorConfig[] = [];
  @Input() public updatesExistingContact = false;

  @Output() public readonly automationChanged = new EventEmitter<Automation>();

  private readonly widgetService = inject(WidgetService);
  private readonly widgetEditorDataService = inject(WidgetEditorDataService);
  private readonly widgetAutomationsService = inject(WidgetAutomationsService);
  private readonly integrationsService = inject(IntegrationsService);
  private readonly serializer = inject(AutomationSerializer);
  private readonly languageService = inject(LanguageService);
  private readonly accountLanguageService = inject(AccountLanguageService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly flyoutService = inject(FlyoutService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  public readonly HandlerRealTimeProp = HandlerRealTimeProp;
  public readonly FormSettingsHandlerTypes = FormSettingsHandlerTypes;

  // Precomputed for the template (refreshed via refreshDerived) so its bindings read fields, not getters/methods.
  public handlerKeys: string[] = [];
  public alternativeApps: ReadonlyArray<string> = ESP_ALTERNATIVE_APPS;
  public allowVerify = false;
  public automationId: string | undefined;
  public previewingKey: string | null = null;

  public shouldShowRealtime = false;
  public hasOutcomes = false;

  public choosableStates: WidgetState[] = [];
  public automationSwitchStateIsOn = false;
  public automationSwitchStateTo: number | undefined;
  public automationSwitchStateDisabled = false;
  private submitLocation: SubmitButtonLocation | null = null;

  public listCheckIsOn = false;
  public listCheckIsDisabled = false;
  public listCheckDisabledMessage: string | undefined;
  public automationAlternateSwitchApp: string | null = null;
  public automationAlternateSwitchListId: string | undefined;
  public automationAlternateSwitchStateId: number | undefined;
  public listCheckAvailableLists: Array<{ id: string; name: string }> = [];
  public listCheckAvailableStates: WidgetState[] = [];
  public listCheckLoadingLists = false;
  public listCheckSaveButtonDisabled = true;

  public realTimeHandler: Record<string, RealTimeHandlerEntry> = {};

  public runAutomationOnlyIfNewSubscriber: boolean | null = null;
  public newSubscriberCheckDisabled = false;
  public newSubscriberBlockedByUpdate = false;

  constructor() {
    this.flyoutService.flyoutHandlerSettingBS
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((setting) => (this.previewingKey = (setting as { key?: string } | undefined)?.key ?? null));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget'] && this.widget) {
      this.rebuildFromWidget();
    }
    if (changes['existingAutomation'] || changes['updatesExistingContact']) {
      this.applyNewSubscriberState();
    }
    if (changes['existingAutomation'] || changes['configuredApps'] || changes['availableApps']) {
      this.updateShowRealtime();
      this.revalidateAlternateSwitching();
      this.checkAlternateSwitchSaveButton();
      this.refreshDerived();
    }
  }

  private rebuildFromWidget(): void {
    if (!this.widget) {
      return;
    }
    this.hasOutcomes = (this.widget.outcomes?.length ?? 0) > 0;
    this.submitLocation = findSubmitButtonLocation(this.widget);
    this.choosableStates = buildChoosableStates(this.widget, this.submitLocation?.stateId, this.languageService);
    this.reinitializeSwitchState();
    this.realTimeHandler = buildHandlerView(this.widget.formSettings, this.submitSwitchStateId(), (key) =>
      this.accountLanguageService.translate(key),
    );
    this.applyPreviewOverlay();
    this.markHandlerStepValidity();
    this.applyNewSubscriberState();
    this.updateShowRealtime();
  }

  private reinitializeSwitchState(): void {
    this.automationSwitchStateDisabled = !(
      this.widget &&
      (this.widget.states?.length ?? 0) > 1 &&
      WidgetComponentHelpers.hasWidgetSubmitButton(this.widget)
    );

    const button = this.submitLocation?.button;
    if (button) {
      this.automationSwitchStateIsOn = !!button.actionValue;
      if (button.actionValue && !isNaN(Number(button.actionValue))) {
        this.automationSwitchStateTo = Number(button.actionValue);
        this.publishSubmitButtonState(button);
      }
    }

    this.automationAlternateSwitchApp = this.widgetState?.alternativeStepApp ?? null;
    this.automationAlternateSwitchListId = this.widgetState?.alternativeStepListId;
    this.automationAlternateSwitchStateId = this.widgetState?.alternativeStepId;
    this.listCheckIsOn = !!this.automationAlternateSwitchApp;
    this.revalidateAlternateSwitching();
    this.reinitializeListCheckData();
  }

  private revalidateAlternateSwitching(): void {
    if ((this.widget?.states?.length ?? 0) < 3) {
      this.listCheckIsDisabled = true;
      this.listCheckDisabledMessage = 'campaign.widget.sidebar.form-settings.at-least-3-states';
      return;
    }
    if (!this.configuredApps?.length && !this.widget?.formRegistrationType) {
      this.listCheckIsDisabled = true;
      this.listCheckDisabledMessage = 'campaign.widget.sidebar.form-settings.no-apps-configured';
      return;
    }
    this.listCheckIsDisabled = false;
    this.listCheckDisabledMessage = undefined;
  }

  private reinitializeListCheckData(): void {
    const submitStateId = this.submitLocation?.stateId ?? this.widgetState?.id;
    this.listCheckAvailableStates = buildChoosableStates(
      this.widget,
      submitStateId,
      this.languageService,
      this.automationSwitchStateTo,
    );
    if (this.automationAlternateSwitchApp && integrationHasLists(this.automationAlternateSwitchApp)) {
      this.listCheckLoadingLists = true;
      this.integrationsService
        .getListForApp(this.automationAlternateSwitchApp)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (lists) => {
            this.listCheckAvailableLists = lists;
            this.listCheckLoadingLists = false;
          },
          error: () => {
            this.listCheckLoadingLists = false;
          },
        });
    } else {
      this.automationAlternateSwitchListId = undefined;
      this.listCheckAvailableLists = [];
    }
    this.checkAlternateSwitchSaveButton();
  }

  private applyNewSubscriberState(): void {
    const state = deriveNewSubscriberState(this.existingAutomation);
    this.runAutomationOnlyIfNewSubscriber = state.value;
    this.newSubscriberCheckDisabled = state.disabled;
    this.newSubscriberBlockedByUpdate = this.updatesExistingContact;
    this.refreshDerived();
  }

  /** Called after a sibling section mutates the shared widget in place — the reference is unchanged, so ngOnChanges won't fire. */
  public refreshMode(): void {
    this.updateShowRealtime();
    this.revalidateAlternateSwitching();
    this.checkAlternateSwitchSaveButton();
    this.refreshDerived();
  }

  private updateShowRealtime(): void {
    if (!this.widget) {
      this.shouldShowRealtime = false;
      return;
    }
    this.shouldShowRealtime =
      (Object.keys(this.realTimeHandler).length > 0 && this.hasRealTimeIntegration()) ||
      !!this.widget.formSettings?.dataSourceId;
  }

  private hasRealTimeIntegration(): boolean {
    return (this.existingAutomation?.actions ?? []).some((action) => action.isRunningInRealTime === true);
  }

  public toggleSwitchStateIsOn(): void {
    const button = this.submitLocation?.button;
    if (!button) {
      return;
    }
    if (this.automationSwitchStateIsOn) {
      if (!this.automationSwitchStateTo) {
        const success = this.choosableStates.find(
          (state) => state.name === 'Success' || (state.type as unknown as string) === 'Success',
        );
        this.automationSwitchStateTo = (success ?? this.choosableStates[0])?.id;
      }
      button.actionType = ButtonActionType.SwitchState;
      button.actionValue = '' + this.automationSwitchStateTo;
      this.publishSubmitButtonState(button);
    } else {
      button.actionType = ButtonActionType.CloseWidget;
      button.actionValue = '';
      this.nextStatesInUse({ submitBtnState: null, alternativeState: null });
    }
    this.updateShowRealtime();
    if (!this.automationSwitchStateIsOn) {
      this.removeAlternateSwitchStateData();
    }
    this.persistSubmitButton(button);
  }

  public updateStateToSwitchTo(): void {
    const button = this.submitLocation?.button;
    if (!button) {
      return;
    }
    button.actionType = ButtonActionType.SwitchState;
    button.actionValue = '' + this.automationSwitchStateTo;
    if (
      this.automationAlternateSwitchStateId &&
      this.automationAlternateSwitchStateId === this.automationSwitchStateTo
    ) {
      this.automationAlternateSwitchStateId = undefined;
      this.saveAlternateSwitchStateData();
    }
    this.reinitializeListCheckData();
    this.publishSubmitButtonState(button);
    this.persistSubmitButton(button);
  }

  private persistSubmitButton(button: ButtonComponent): void {
    if (!this.submitLocation) {
      return;
    }
    this.widgetEditorDataService
      .persistEditedComponent(
        { ...button, containerId: this.submitLocation.containerId },
        false,
        this.submitLocation.stateId,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.markUnpublished(),
        error: () => this.snackBarService.error('generic.errorOccurred'),
      });
  }

  private publishSubmitButtonState(button: ButtonComponent): void {
    this.nextStatesInUse({
      submitBtnState: {
        linkedState: +(button.actionValue ?? 0),
        sourceText: this.languageService.translateInstantly('campaign.widget.sidebar.layout.form-settings'),
        sourceStateOrder: null,
        sourceStateId: this.widgetState?.id,
        sourceComponentId: button.id,
      },
    });
    this.flyoutFormSettingsNext({ submitButton: button.actionValue });
  }

  public toggleSwitchStateCheck(): void {
    this.markUnpublished();
    this.checkAlternateSwitchSaveButton();
    if (!this.listCheckIsOn) {
      this.removeAlternateSwitchStateData();
    }
  }

  public updateAlternateAppToSwitchTo(): void {
    this.automationAlternateSwitchListId = undefined;
    this.reinitializeListCheckData();
    this.refreshDerived();
  }

  public updateAlternateSelection(): void {
    this.checkAlternateSwitchSaveButton();
  }

  private checkAlternateSwitchSaveButton(): void {
    this.listCheckSaveButtonDisabled =
      !this.automationAlternateSwitchApp ||
      (!this.hasRealTimeIntegration() &&
        !this.automationAlternateSwitchListId &&
        integrationHasLists(this.automationAlternateSwitchApp)) ||
      !this.automationAlternateSwitchStateId;
  }

  public saveAlternateSwitchStateData(): void {
    const app =
      this.automationAlternateSwitchApp === IntegrationType.RuleMailer
        ? AudienceFilterApp.Rule
        : this.automationAlternateSwitchApp;
    this.writeAlternateStep(
      app ?? undefined,
      integrationHasLists(app) ? this.automationAlternateSwitchListId : undefined,
      this.automationAlternateSwitchStateId,
    );

    this.nextStatesInUse({
      alternativeState: {
        linkedState: this.automationAlternateSwitchStateId ?? 0,
        sourceText: this.languageService.translateInstantly('campaign.widget.sidebar.layout.form-settings'),
        sourceStateOrder: null,
        sourceStateId: this.widgetState?.id,
        sourceComponentId: this.submitLocation?.button?.id,
      },
    });
    this.flyoutFormSettingsNext({
      alternativeStateUsed: this.automationAlternateSwitchStateId,
      componentId: this.widgetState?.id,
    });
    this.persistWidgetState('generic.success');
  }

  private removeAlternateSwitchStateData(): void {
    this.writeAlternateStep(undefined, undefined, undefined);
    this.automationAlternateSwitchApp = null;
    this.automationAlternateSwitchListId = undefined;
    this.automationAlternateSwitchStateId = undefined;
    this.nextStatesInUse({ alternativeState: null });
    this.flyoutFormSettingsNext({ alternativeStateUsed: '' });
    this.persistWidgetState();
    this.refreshDerived();
  }

  private writeAlternateStep(app: string | undefined, listId: string | undefined, stateId: number | undefined): void {
    this.widgetState.alternativeStepApp = app;
    this.widgetState.alternativeStepListId = listId;
    this.widgetState.alternativeStepId = stateId;
    this.widgetState.subscriberCheckApp = null;
    this.widgetState.subscriberCheckListId = null;
    this.widgetState.subscriberCheckStateId = null;
  }

  private persistWidgetState(successKey?: string): void {
    this.widgetEditorDataService
      .persistEditedComponent(this.widgetState as unknown as WidgetComponent, false, this.widgetState?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.markUnpublished();
          if (successKey) {
            this.snackBarService.success(successKey);
          }
        },
        error: () => this.snackBarService.error('generic.errorOccurred'),
      });
  }

  // Refreshes the precomputed template fields. Called wherever their inputs change (widget rebuild, automation /
  // alt-app changes). previewingKey is driven separately by the flyoutHandlerSetting subscription below.
  private refreshDerived(): void {
    this.handlerKeys = HANDLER_ORDER.filter((key) => !!this.realTimeHandler[key]);
    const connectedApps = new Set(this.availableApps.map((app) => app.name));
    const espApps = ESP_ALTERNATIVE_APPS.filter((name) => connectedApps.has(name));
    const registrationApp = ECOMMERCE_ALTERNATIVE_APPS.find((name) => name === this.widget?.formRegistrationType);
    this.alternativeApps = registrationApp ? [...espApps, registrationApp] : espApps;
    this.allowVerify = this.allowVerifyAsNewSubscriber();
    this.automationId = this.existingAutomation?.id;
  }

  // Stays a getter: must re-evaluate live as the user edits steps in this section, which mutate the handler
  // model in place without notifying a signal.
  public get realTimeHandlerSaveDisabled(): boolean {
    return isHandlerSaveDisabled(this.realTimeHandler, this.choosableStateIds());
  }

  public onHandlerChange(key: string): void {
    applyHandlerActionChange(this.realTimeHandler[key]);
  }

  public saveRealTimeHandlerData(): void {
    if (!this.widget) {
      return;
    }
    const payload = toFormSettingsPayload(this.realTimeHandler, this.widget.formSettings?.dataSourceId);
    this.widgetService
      .patchWidget(this.widget.id, '/formSettings', payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.widget) {
            this.widget.formSettings = payload;
            this.widgetEditorDataService.widgetBS.next({ ...this.widget });
          }
          // The real-time handler supersedes a plain switch-state action — turn it off if it was on.
          if (this.automationSwitchStateIsOn) {
            this.automationSwitchStateIsOn = false;
            this.toggleSwitchStateIsOn();
          }
          this.markUnpublished();
          this.snackBarService.success('generic.success');
        },
        error: () => this.snackBarService.error('generic.errorOccurred'),
      });
  }

  public isPreviewing(key: string): boolean {
    return this.flyoutHandlerSettingKey() === key;
  }

  public togglePreview(key: string): void {
    this.flyoutService.flyoutHandlerSettingBS.next(
      this.isPreviewing(key) ? null : { key, value: this.realTimeHandler[key] },
    );
  }

  private applyPreviewOverlay(): void {
    const key = this.flyoutHandlerSettingKey();
    if (key && this.realTimeHandler[key]) {
      this.flyoutService.flyoutHandlerSettingBS.next({ key, value: this.realTimeHandler[key] });
    }
  }

  private markHandlerStepValidity(): void {
    markInvalidSteps(this.realTimeHandler, this.choosableStateIds());
  }

  private choosableStateIds(): ReadonlySet<number> {
    return new Set(this.choosableStates.map((state) => state.id));
  }

  public allowVerifyAsNewSubscriber(): boolean {
    if (!this.existingAutomation?.id) {
      return false;
    }
    return (
      this.automationAlternateSwitchApp !== AlternativeStepIntegration.Centra &&
      this.automationAlternateSwitchApp !== AlternativeStepIntegration.Shopify
    );
  }

  public toggleSubscriberCheckSetting(): void {
    if (this.newSubscriberCheckDisabled || this.newSubscriberBlockedByUpdate || !this.existingAutomation) {
      return;
    }
    const enabled = this.runAutomationOnlyIfNewSubscriber !== true;
    const updated = this.serializer.setNewSubscriberOnly(this.existingAutomation, enabled);
    this.widgetAutomationsService
      .updateAutomation(updated)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.runAutomationOnlyIfNewSubscriber = enabled;
          this.existingAutomation = saved;
          this.refreshDerived();
          this.automationChanged.emit(saved);
          this.markUnpublished();
          this.snackBarService.success('generic.success');
        },
        error: () => this.snackBarService.error('generic.errorOccurred'),
      });
  }

  public showOutcomes(): void {
    this.dialog.open(OutcomeLogicComponent, { width: '800px', data: { widget: this.widget } });
  }

  private markUnpublished(): void {
    this.widgetService.hasUnpublishedChangesBS.next(true);
  }

  private nextStatesInUse(patch: Record<string, unknown>): void {
    this.widgetService.statesInUseBS.next({ ...this.widgetService.statesInUseBS.value, ...patch } as StatesInUse);
  }

  private submitSwitchStateId(): number | null {
    const button = this.submitLocation?.button;
    return button?.actionType === ButtonActionType.SwitchState && button.actionValue ? +button.actionValue : null;
  }

  private flyoutHandlerSettingKey(): string | undefined {
    return (this.flyoutService.flyoutHandlerSetting as { key?: string } | undefined)?.key;
  }

  private flyoutFormSettingsNext(value: unknown): void {
    this.flyoutService.flyoutFormSettingsBS.next(value);
  }
}
